import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import { COMPANY } from "../data/content";
import { Reveal, SectionTag, PageHeader } from "../components/Shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = { name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll get back to you within one business day.");
      setForm(INITIAL);
    } catch {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  const items = [
    { icon: MapPin, label: "Facility", value: COMPANY.address, color: "var(--brand-orange)", testid: "contact-address" },
    { icon: Phone, label: "Phone", value: COMPANY.phone, href: `tel:${COMPANY.phoneRaw}`, color: "var(--brand-magenta)", testid: "contact-phone" },
    { icon: Mail, label: "Email", value: COMPANY.email, href: `mailto:${COMPANY.email}`, color: "var(--brand-cyan)", testid: "contact-email" },
    { icon: Clock, label: "Hours", value: COMPANY.hours, color: "var(--brand-lime)", testid: "contact-hours" },
  ];

  return (
    <div data-testid="contact-page">
      <PageHeader
        tag="Contact Us"
        title={<>Let's Talk<br />Signage.</>}
        subtitle="Questions, site surveys, or project specs — reach out and a real person from our Woodbridge team will respond within one business day."
      />

      <section className="pb-28">
        <div className="msg-container grid grid-cols-1 lg:grid-cols-5 gap-16">
          <Reveal className="lg:col-span-2">
            <div className="space-y-8">
              {items.map((it) => (
                <div key={it.label} className="flex gap-5" data-testid={it.testid}>
                  <span className="w-12 h-12 shrink-0 flex items-center justify-center border" style={{ borderColor: "var(--line)" }}>
                    <it.icon size={18} style={{ color: it.color }} />
                  </span>
                  <div>
                    <p className="font-mono text-[10px] tracking-[0.2em] uppercase" style={{ color: "var(--text-3)" }}>{it.label}</p>
                    {it.href ? (
                      <a href={it.href} className="font-display font-bold text-base mt-1 block hover:text-[var(--brand-cyan)] transition-colors">{it.value}</a>
                    ) : (
                      <p className="font-display font-bold text-base mt-1">{it.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-12 p-8 relative overflow-hidden" style={{ background: "var(--bg-2)", border: "1px solid var(--line)" }}>
              <span className="corner-notch" />
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase" style={{ color: "var(--text-3)" }}>Serving</p>
              <p className="font-display font-bold text-lg mt-2">Woodbridge · Vaughan · Toronto · GTA · Ontario-wide</p>
            </div>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-3">
            <form onSubmit={submit} className="msg-card p-8 lg:p-10" data-testid="contact-form">
              <SectionTag>Send a Message</SectionTag>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8">
                <div>
                  <label className="msg-label">Full Name *</label>
                  <input className="msg-input" required value={form.name} onChange={set("name")} placeholder="Jane Smith" data-testid="contact-name-input" />
                </div>
                <div>
                  <label className="msg-label">Email *</label>
                  <input className="msg-input" type="email" required value={form.email} onChange={set("email")} placeholder="jane@company.com" data-testid="contact-email-input" />
                </div>
                <div>
                  <label className="msg-label">Phone</label>
                  <input className="msg-input" value={form.phone} onChange={set("phone")} placeholder="647-000-0000" data-testid="contact-phone-input" />
                </div>
                <div>
                  <label className="msg-label">Subject *</label>
                  <input className="msg-input" required value={form.subject} onChange={set("subject")} placeholder="Storefront signage inquiry" data-testid="contact-subject-input" />
                </div>
              </div>
              <div className="mt-6">
                <label className="msg-label">Message *</label>
                <textarea className="msg-textarea" rows={6} required value={form.message} onChange={set("message")} placeholder="Tell us about your project…" data-testid="contact-message-input" />
              </div>
              <button type="submit" className="btn-brand mt-8" disabled={sending} data-testid="contact-submit-btn">
                {sending ? "Sending…" : "Send Message"} <Send size={15} />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
